import { request } from './api.ts';
import type {
  BillingProduct,
  BillingSummary,
  PlatformBillingProduct,
  PlatformBillingProductUpdate,
  TenantPlan,
} from './dto.ts';

export type {
  BillingProduct,
  BillingStatus,
  BillingSummary,
  PlatformBillingProduct,
  PlatformBillingProductUpdate,
  ProductKind,
  TenantPlan,
} from './dto.ts';

/**
 * Billing calls, kept out of `api.ts` because that file is at its size ceiling.
 *
 * Money never touches this console. Checkout and the portal answer with a URL
 * on Stripe's own domain and the browser is sent there; no card number, and no
 * field that could hold one, is ever rendered here.
 */
export const billingApi = {
  /** The products an account can buy, active only, in the server's order. */
  billingCatalogue: () => request<{ products: BillingProduct[] }>('/billing/catalogue'),

  billingSummary: () => request<BillingSummary>('/billing/summary'),

  /**
   * The whole plan, not a delta. The server validates the combination (one
   * base, suite or individual frameworks, at most one scan pack) and answers
   * with the entitlement it derived, which is the only version of it the
   * console should display.
   */
  setTenantPlan: (tenantId: string, productIds: readonly string[]) =>
    request<{ plan: TenantPlan }>(`/tenants/${encodeURIComponent(tenantId)}/plan`, {
      method: 'PUT',
      body: JSON.stringify({ productIds: [...productIds] }),
    }),

  billingCheckout: () => request<{ url: string }>('/billing/checkout', { method: 'POST' }),

  billingPortal: () => request<{ url: string }>('/billing/portal', { method: 'POST' }),

  /**
   * An address rather than a call. The browser downloads it with the session
   * cookie attached, which a fetch-and-blob would have to reimplement, and it
   * sits under the same `/api` prefix as every other request.
   */
  billingExportUrl: (): string => '/api/billing/export.csv',

  /** Staff only: every product, retired ones included, with its Stripe price. */
  platformBillingProducts: () =>
    request<{ products: PlatformBillingProduct[] }>('/platform/billing/products'),

  updatePlatformBillingProduct: (id: string, body: PlatformBillingProductUpdate) =>
    request<{ product: PlatformBillingProduct }>(
      `/platform/billing/products/${encodeURIComponent(id)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
};
